import { createContext } from '@radix-ui/react-context';
import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { createStore, StoreApi } from 'zustand';
import { parseAsStringLiteral, useQueryState } from 'nuqs';

type AlertContext = {
  // Provide a zustand store to enable precise reactivity
  store: StoreApi<AlertState>;
  setIsMaximized: (maximized: boolean) => void;
  setHiddenCameras: (cameras: string[] | Set<string>) => void;
  toggleCamera: (camera: { id: string; name: string }) => void;
  setIsBoundingBoxVisible: (visible: boolean) => void;
  toggleBoundingBoxVisible: () => void;
  setIsActivationZoneVisible: (visible: boolean) => void;
  toggleActivationZoneVisible: () => void;
};

const [Provider, useContext] = createContext<AlertContext>('Alert');

const tabParser = parseAsStringLiteral(['event', 'playback'] as const).withDefault('event');

/**
 * Reads/writes the alert tab via the `?tab=` query param. Backed by URL state
 * so that navigating to a new alert (which uses an absolute path with no query
 * string) always resets to the screenshot tab, while a refresh on the current
 * alert preserves the user's selection and shareable links work.
 */
export const useAlertTab = () => {
  const [activeTab, setActiveTab] = useQueryState('tab', tabParser);
  const toggleTab = useCallback(
    () => setActiveTab((prev) => (prev === 'event' ? 'playback' : 'event')),
    [setActiveTab]
  );
  return { activeTab, setActiveTab, toggleTab };
};

export const AlertProvider = (props: PropsWithChildren) => {
  const [store] = useState(() => {
    return createStore<AlertState>(() => ({
      isMaximized: false,
      hiddenCameras: new Set(),
      isBoundingBoxVisible: true,
      isActivationZoneVisible: false,
    }));
  });

  const setIsMaximized: AlertContext['setIsMaximized'] = useCallback(
    (maximized) => {
      store.setState({ isMaximized: maximized });
    },
    [store]
  );

  const setHiddenCameras: AlertContext['setHiddenCameras'] = useCallback(
    (cameras) => {
      store.setState({ hiddenCameras: new Set(cameras) });
    },
    [store]
  );

  const toggleCamera: AlertContext['toggleCamera'] = useCallback(
    (camera) => {
      store.setState((state) => {
        const newHiddenCameras = new Set(state.hiddenCameras);

        if (newHiddenCameras.has(camera.id)) {
          newHiddenCameras.delete(camera.id);
        } else {
          newHiddenCameras.add(camera.id);
        }

        return { hiddenCameras: newHiddenCameras };
      });
    },
    [store]
  );

  const setIsBoundingBoxVisible: AlertContext['setIsBoundingBoxVisible'] = useCallback(
    (visible) => {
      store.setState({ isBoundingBoxVisible: visible });
    },
    [store]
  );

  const setIsActivationZoneVisible: AlertContext['setIsActivationZoneVisible'] = useCallback(
    (visible) => {
      store.setState({ isActivationZoneVisible: visible });
    },
    [store]
  );

  const toggleBoundingBoxVisible: AlertContext['toggleBoundingBoxVisible'] = useCallback(() => {
    store.setState((state) => ({
      isBoundingBoxVisible: !state.isBoundingBoxVisible,
    }));
  }, [store]);

  const toggleActivationZoneVisible: AlertContext['toggleActivationZoneVisible'] =
    useCallback(() => {
      store.setState((state) => ({
        isActivationZoneVisible: !state.isActivationZoneVisible,
      }));
    }, [store]);

  return (
    <Provider
      store={store}
      setIsMaximized={setIsMaximized}
      setHiddenCameras={setHiddenCameras}
      toggleCamera={toggleCamera}
      setIsBoundingBoxVisible={setIsBoundingBoxVisible}
      setIsActivationZoneVisible={setIsActivationZoneVisible}
      toggleBoundingBoxVisible={toggleBoundingBoxVisible}
      toggleActivationZoneVisible={toggleActivationZoneVisible}
    >
      {props.children}
    </Provider>
  );
};

export type AlertTabKey = 'event' | 'playback';

type AlertState = {
  isMaximized: boolean;
  /** When this list is empty, all cameras are considered selected */
  hiddenCameras: Set<string>;
  isBoundingBoxVisible: boolean;
  isActivationZoneVisible: boolean;
};

export const useAlertCtx = () => {
  return useContext('useAlertCtx');
};